// Entity types — the vocabulary of LifeOS.
//
// Rules these obey, from blueprint/03-DATA-MODEL.md and docs/research/backend-validation.md:
//   · Every field that can be absent in real life is nullable. A personal system that cannot
//     render a gap honestly will lie to you eventually.
//   · Enum-ish fields are string unions here and `text` + a check constraint in Postgres —
//     never a Postgres enum (they do not alter cleanly).
//   · Transcripts carry per-segment `startMs`/`endMs`. This is the citation convention: without
//     it a chat answer can only say "Lecture 4", never "Lecture 4 · 12:31".

export type ISODate = string // 'YYYY-MM-DD'
export type ISOTimestamp = string // full ISO 8601, always UTC ('…Z')

/** 0 = Sunday … 6 = Saturday. Matches `Date.getUTCDay()`. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6

// ── School ──────────────────────────────────────────────────────────────────

/**
 * The academic term. Its dates are what turn a date into "Week 4" and what tell the schedule
 * which days have no instruction. Owned by Canvas once the connector exists (V2).
 */
export interface Term {
  name: string
  instructionStarts: ISODate
  instructionEnds: ISODate
  /** Campus holidays inside the term — no classes, and no lectures generated. */
  holidays: ISODate[]
}

export type MeetingKind = 'lecture' | 'discussion' | 'lab'

export interface CourseMeeting {
  kind: MeetingKind
  weekday: Weekday
  /** Minutes past local midnight. Times are the owner's schedule, not a timezone puzzle. */
  startMinutes: number
  endMinutes: number
  location: string
}

export interface Course {
  id: string
  /** 'COGSCI 175' */
  code: string
  name: string
  /** Canvas's first listed teacher. Null when it lists none — never 'Unknown'. */
  instructor: string | null
  /**
   * CANVAS DOES NOT SUPPLY THIS. The Course object has no credit-hours field (verified against
   * the live bCourses API, 2026-08-26: 33 keys, none of them units), so every synced course
   * carries null and the card simply omits the "N units" segment. The field survives for a
   * manual entry; it is never guessed from the course code.
   */
  units: number | null
  term: string
  /**
   * EMPTY MEANS UNKNOWN, NOT "NEVER MEETS". Berkeley's class meeting times live in SIS and reach
   * a student through bCal, not through Canvas — so a Canvas-synced course has no meeting
   * pattern at all, and the card says "no meeting times in Canvas" rather than implying the
   * course has finished (docs/research/berkeley-data-sources.md §B.7).
   */
  meetings: CourseMeeting[]
  /**
   * Canvas `computed_current_score`, 0–100. Null when the instructor set `hide_final_grades` —
   * in that case `gradeHidden` is true and the UI must say "grade hidden by instructor",
   * never render a blank.
   */
  gradeSoFar: number | null
  /**
   * Canvas `computed_current_grade` — the letter the COURSE assigns, not one we worked out from
   * `gradeSoFar`. Grading scales differ per course, so deriving 'B+' from 88.4 would be inventing
   * a fact. Null whenever Canvas gives none, and then only the percentage is shown.
   */
  gradeLetter: string | null
  gradeHidden: boolean
  unreadAnnouncements: number
}

/** One concrete occurrence of a CourseMeeting on a date. What the Today screen lists. */
export interface ClassOccurrence {
  courseId: string
  courseCode: string
  courseName: string
  kind: MeetingKind
  date: ISODate
  startMinutes: number
  endMinutes: number
  location: string
}

/**
 * CANVAS HAS NO ASSIGNMENT-TYPE TAXONOMY. Its Assignment object carries `submission_types`,
 * which says how work is HANDED IN, not what it is — 'online_upload' is an essay and a problem
 * set and a lab report. So `unclassified` is the honest kind and the common one for anything
 * that came down the connector; only 'quiz' is ever inferred, and only where Canvas states it
 * outright. Inventing 'essay' from a filename is exactly the plausible fabrication this product
 * exists not to do. The other six remain for hand-entered and demo data.
 */
export type AssignmentKind =
  | 'reading_response'
  | 'essay'
  | 'quiz'
  | 'problem_set'
  | 'exam'
  | 'project'
  | 'unclassified'

export type AssignmentStatus = 'upcoming' | 'submitted' | 'graded' | 'missing'

export interface Assignment {
  id: string
  courseId: string
  courseCode: string
  title: string
  kind: AssignmentKind
  /**
   * NULL IS THE NORMAL CASE, not an error state: 20 of the owner's 24 live Canvas assignments
   * carried no `due_at` at all on 2026-08-26. An undated assignment is a real assignment — it
   * appears in the course's table with "no due date in Canvas" in the DUE cell — and it is
   * simply absent from the deadline runway, which is a list about time and cannot place it.
   * Use `datedAssignments()` (school.ts) at any boundary that needs a date to exist.
   */
  dueAt: ISOTimestamp | null
  /**
   * When the work was handed out — Canvas's `unlock_at`, falling back to `created_at`.
   * Null when Canvas supplies neither, and then the Today screen's elapsed bar is simply not
   * drawn: a bar with a guessed start is a lie about how much time is left.
   */
  assignedAt: ISOTimestamp | null
  status: AssignmentStatus
  /** Points earned. Null unless status is 'graded'. */
  score: number | null
  pointsPossible: number
  // ── THE DETAIL PEEK'S FIELDS (2026-08-29) ────────────────────────────────
  //
  // The five below were on the WIRE all along (`AssignmentSchema` in
  // apps/worker/src/lib/school.ts has carried `lockAt`, `gradingType`, `submissionTypes` and
  // `htmlUrl` since the connector shipped) and were simply not in the browser's vocabulary,
  // because no screen had anywhere to put them. A row you can click has: everything Canvas
  // stated about a piece of work, in one panel, and a link out to bCourses for the rest.
  //
  // Every one of them is NULLABLE and every null means "Canvas said nothing", never zero and
  // never a default. `gradingType: null` is not 'points'; `lockAt: null` is not "never locks".
  /** When Canvas stops accepting the work. Null when the professor set no lock date. */
  lockAt: ISOTimestamp | null
  /** Canvas's own word: 'points' · 'percent' · 'pass_fail' · 'letter_grade'. Never derived. */
  gradingType: string | null
  /** How it is handed in ('online_upload', 'online_quiz', 'on_paper'). Empty when Canvas said none. */
  submissionTypes: string[]
  /** The bCourses permalink. Null for hand-entered work, which has no page to open. */
  htmlUrl: string | null
  /**
   * WHAT THE ASSIGNMENT ACTUALLY ASKS FOR — the prose under the title on the bCourses page,
   * as plain text (the Worker strips Canvas's HTML the same way it strips an announcement's).
   *
   * EMPTY STRING IS A REAL AND COMMON STATE: plenty of Canvas assignments are a title, a date
   * and nothing else. The peek says so in words rather than drawing an empty box, and never
   * fills the gap with the title again.
   */
  description: string
}

/**
 * The pipeline states a recorded lecture moves through (V1 transcription system).
 * `failed` is the honest fifth: a transcription that gave up says so rather than sitting
 * in `processing` forever looking like it is still working.
 */
export type LectureStatus =
  | 'not_recorded'
  | 'audio_uploaded'
  | 'processing'
  | 'transcribed'
  | 'failed'

export interface Lecture {
  id: string
  courseId: string
  courseCode: string
  /**
   * 1-based lecture number within the course — what a citation says out loud.
   *
   * NULL IS A REAL STATE, and widening it here (2026-08-26) is the convergence the
   * `CapturedLecture` comment in apps/web/src/lib/data/types.ts predicted. Nothing in the
   * pipeline numbers a recording: the drop folder knows a filename, the phone knows a filename,
   * and only a hand-written `POST /api/lectures` ever sets an index. The alternatives were both
   * lies — invent "Lecture 3" by counting rows (wrong the first time a class is missed) or drop
   * the row from the course page (a lecture the owner recorded, invisible). So an unnumbered
   * lecture renders under its own title and its date, and is addressed by id.
   */
  index: number | null
  /**
   * What the recording is called. Null when nothing named it — a title invented for an
   * unrecorded class is a guess about what a class you did not hear was about.
   */
  title: string | null
  date: ISODate
  durationSec: number | null
  status: LectureStatus
  /** Where the raw audio lives once the PC worker is done with it. */
  audioLocation: 'none' | 'cloud' | 'archived_on_pc'
}

export interface TranscriptSegment {
  id: string
  transcriptId: string
  /** 0-based position. Segments are ordered and never overlap. */
  index: number
  startMs: number
  endMs: number
  speaker: string | null
  text: string
  /**
   * How sure the transcriber was of this passage — the duration-weighted mean of Whisper's
   * `avg_logprob` over the segments in this group. Negative, and closer to 0 means surer.
   *
   * NULL IS A REAL ANSWER: the transcript predates this being recorded, or the transcriber
   * reported nothing. Null must never be rendered or treated as 0 — 0 is perfect certainty, so
   * the substitution turns "unknown" into the most confident claim on the screen.
   *
   * NOTHING IN THE UI READS THIS YET, on purpose. It is the transcriber's opinion of itself, not
   * a measured error rate, and the threshold that would justify a "low confidence" label has to
   * come from the accuracy harness in apps/pc-worker/eval/ rather than from a guess.
   */
  avgLogprob: number | null
}

/**
 * ONE VOICE IN A RECORDING — and the honest limit of what is known about it.
 *
 * The desktop worker separates the voices in a lecture. It cannot identify anyone: there is no
 * recording of any professor to compare against, and asking the owner to enrol his professors'
 * voices is friction the feature does not need. So one named rule is applied — THE VOICE THAT
 * TALKS THE MOST ACROSS THE WHOLE RECORDING IS MOST LIKELY THE LECTURER — and it travels with
 * its answer instead of hiding behind the word "Professor".
 *
 * Which is why every surface that renders this says "Professor (likely)" and shows the share it
 * was decided on. The rule is wrong exactly where you would expect: a discussion section, a
 * seminar, a recording that is mostly the owner's own group work. A reader who knows that can
 * discount it — but only if the app told them what it did.
 */
export interface TranscriptSpeaker {
  /**
   * Exactly the string on `TranscriptSegment.speaker` for this voice, so a header can be matched
   * to the passages under it: `professor` for the dominant one, then `speaker-2`, `speaker-3`, …
   * in descending talk time. The others are deliberately unnamed — "a different voice from the
   * professor's" is the whole of what was established.
   */
  label: string
  role: 'professor' | 'other'
  /** Seconds this voice was speaking, across the whole recording. */
  talkSec: number
  /** This voice's fraction of all DETECTED SPEECH, 0–1. Not of the recording's length: silence belongs to nobody. */
  share: number
}

export interface Transcript {
  id: string
  /** 'lecture' segments cite a lecture; 'voice_memo' segments cite a journal entry. */
  source: 'lecture' | 'voice_memo'
  lectureId: string | null
  wordCount: number
  language: string
  /**
   * The voices in this recording, ordered by talk time descending.
   *
   * NULL MEANS NOBODY WORKED OUT WHO WAS SPEAKING — every transcript from before 2026-09-02,
   * every voice memo (a memo is one person, so calling its dominant voice "professor" would be a
   * confident lie), and any lecture whose diarization failed. Null renders as NOTHING: no
   * header, no "unknown speaker" filler, no empty list standing in for "there were no voices".
   * Hidden ≠ missing (CLAUDE.md → design system invariants).
   */
  speakers: TranscriptSpeaker[] | null
  /**
   * The transcriber. A transcript is a machine-made artifact like any other, and the lecture
   * page says so out loud ('11,240 words · Whisper large-v3') — a transcript whose model is
   * unknown is a transcript nobody can weigh.
   *
   * Null when the row does not record one (the column is nullable, and a transcript written
   * before the worker reported its model exists). The page then prints the word count alone
   * rather than naming a transcriber nobody verified: an absent field is absent from the UI.
   */
  model: string | null
  createdAt: ISOTimestamp
}

// ── Audio pipeline (V1 transcription system) ────────────────────────────────
//
// The queue between a phone that records and a desktop GPU that transcribes. Its shape is
// dictated by one rule from docs/research/transcription-pipeline.md: a 30-second voice memo
// must never wait behind a 78-minute lecture.

/** Set at capture time by the surface that knows — never inferred from the audio. */
export type AudioJobKind = 'memo' | 'lecture'

/**
 * The hard ceiling on ONE upload, in bytes: Cloudflare's Workers request-body limit on the Free
 * and Pro plans. It is not a setting this application chose, and no code here can raise it.
 *
 * IT LIVES IN SHARED BECAUSE TWO SIDES ENFORCE IT. The Worker refuses a larger body with a 413
 * (apps/worker/src/routes/audio.ts, which documents at length why the bytes go through the
 * Worker at all), and the upload screen refuses the file before a byte leaves the phone — a
 * four-minute upload that ends in a rejection is the worst version of this. Two copies of the
 * number would eventually disagree, and the disagreement would look like a bug in the app rather
 * than a stale constant.
 *
 * For scale: a 78-minute lecture at 64 kbps mono is ~37 MB, at 128 kbps ~75 MB. The ceiling is
 * real but not usually close.
 */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024

/**
 * `awaiting_audio` is the gap between "the job row exists" and "the bytes are in R2".
 * A job in that state is invisible to the PC worker, which is the point.
 *
 * `uploading` is the narrower gap inside it: ONE attempt holds the job and its bytes are in
 * flight. Also invisible to the PC worker, and that is a second point — a job whose bytes are
 * half-written must not be claimable, and a second PUT arriving meanwhile is refused rather than
 * allowed to overwrite the first upload's object.
 */
export type AudioJobStatus =
  | 'awaiting_audio'
  | 'uploading'
  | 'queued'
  | 'claimed'
  | 'transcribing'
  | 'done'
  | 'failed'
  | 'cancelled'

/** Which surface uploaded it. Debugging a pipeline that spans four devices needs this. */
export type AudioJobOrigin = 'phone' | 'desktop' | 'drop_folder' | 'web'

export interface AudioJob {
  id: string
  kind: AudioJobKind
  status: AudioJobStatus
  /**
   * Lower runs first. Memos occupy 0–49 and lectures 50–99, enforced by the API, so
   * "memos first" is a guarantee of the number range rather than a hope about defaults.
   */
  priority: number
  origin: AudioJobOrigin
  /** Where the audio lives in R2 until it is transcribed, then nowhere. */
  r2Key: string
  durationSec: number | null
  bytes: number | null
  /** The day a voice memo belongs to. Null for lectures. */
  journalDate: ISODate | null
  claimedAt: ISOTimestamp | null
  claimedBy: string | null
  attempts: number
  /** Plain words, for the owner. Never a stack trace. */
  lastError: string | null
  /**
   * The uploading client's own name for this recording — minted before it registered, reused on
   * every retry of it, and what makes a retry converge on this row instead of creating a second.
   *
   * Null means the caller does not name its uploads (the PC drop folder, the desktop recorder),
   * which is a supported way to register and never "we lost it".
   */
  clientUploadId: string | null
  /**
   * When the attempt currently holding this job acquired it; null when none does.
   *
   * The attempt's own fencing token is NOT on the wire on purpose: it decides who may publish
   * the bytes, and a token a client can read is a token a client will eventually send back.
   */
  uploadStartedAt: ISOTimestamp | null
  createdAt: ISOTimestamp
  updatedAt: ISOTimestamp
}

/** A long-lived token for the surfaces that cannot do OAuth: the phone, the PC worker. */
export type CaptureTokenDevice =
  | 'phone'
  | 'extension'
  | 'tablet'
  | 'desktop'
  | 'pc_worker'
  | 'unknown'

/**
 * THE ONE CaptureToken. (Two parallel port lanes each declared one of these on 2026-08-25 —
 * a strict-enum model shape and a display shape — and the merge collided. This is the union
 * of both, and the lesson: model truth in `device`, human name in `label`, never one field
 * doing both jobs.)
 */
export interface CaptureToken {
  id: string
  /** Display name, e.g. 'iPhone · Shortcut'. */
  label: string
  /** Model truth — matches the DB check constraint. Picks the glyph; not free text. */
  device: CaptureTokenDevice
  lastUsedAt: ISOTimestamp | null
  /** Null until the Worker tracks per-token counts — render nothing, never 0. */
  capturesThisMonth: number | null
  /**
   * Set once revoked. A revoked token is REPORTED afterwards, never silently dropped from
   * the list — "captures from it now fail loudly" is the point.
   */
  revokedAt: ISOTimestamp | null
  createdAt: ISOTimestamp
}

// ── Journal ─────────────────────────────────────────────────────────────────

export interface JournalEntry {
  /** One entry per day. `(user_id, date)` is the idempotency key. */
  date: ISODate
  /** The prompt shown when the entry was written. Deterministic, never re-rolled. */
  prompt: string
  text: string
  wordCount: number
  /** 1–5, or null. Skippable by design: blank when skipped, never nagging. */
  mood: number | null
  energy: number | null
  tags: string[]
  voiceMemoCount: number
  updatedAt: ISOTimestamp
}

/**
 * The states a voice memo moves through, and the reason the row exists at all: a memo that
 * has been recorded but not transcribed is NOT a memo with an empty transcript. The queued state
 * names the thing it is waiting on (the desktop worker), because "waiting" with no named cause is
 * indistinguishable from broken.
 *
 * `failed` was added when the recorder went live (V1). The handoff draws three states, but the
 * pipeline has a fourth: audio that reached R2 and that the desktop worker gave up on after
 * `MAX_ATTEMPTS`. Rendering that as `queued` would say "waiting for desktop worker" about a memo
 * nothing is waiting on, and omitting it would delete a real recording from the screen — both are
 * the app faking knowledge. The row carries the worker's own plain-words reason with it.
 */
export type VoiceMemoStatus = 'recording' | 'queued' | 'transcribed' | 'failed'

export interface VoiceMemo {
  id: string
  /** The journal day it was dropped into. */
  date: ISODate
  /** When recording started. Rendered off the string, never through UTC. */
  recordedAt: ISOTimestamp
  durationSec: number
  status: VoiceMemoStatus
  /** Null until the desktop worker returns one. Absent, not empty. */
  transcriptId: string | null
}

// ── Tasks ───────────────────────────────────────────────────────────────────

export type TaskPriority = 'none' | 'low' | 'medium' | 'high'

export interface Task {
  id: string
  title: string
  done: boolean
  due: ISODate | null
  /** 'School admin' | 'Apartment' | 'Errands' | 'Inbox' — owner-managed, not free text. */
  list: string
  priority: TaskPriority
  /** RFC 5545 RRULE, expanded in the Worker (never in SQL). Null = one-off. */
  rrule: string | null
  reminderAt: ISOTimestamp | null
  createdAt: ISOTimestamp
  completedAt: ISOTimestamp | null
  /**
   * How long the owner says this will take, in minutes. NULL means no estimate was given, and
   * nothing infers one from the title, the list or the priority.
   *
   * It exists because the Calendar's suggestion rule cannot exist without it: a suggested block
   * needs a length, the only honest length is one a human supplied, and a task without an
   * estimate therefore gets no suggestion at all rather than a plausible hour
   * (docs/design/calendar-brief.md rule 2).
   */
  estimateMin: number | null
  /**
   * Set when this reminder was created by accepting a chat proposal — the id of that proposal.
   * Null for everything else, which is every task typed in, captured, or materialised by a
   * recurrence. The Reminders row reads it to say "from chat".
   */
  proposalId: string | null
}

// ── Learning ────────────────────────────────────────────────────────────────

/**
 * What kind of thing a resource is. The plan says which; the app never guesses one from a URL,
 * because "youtube.com/watch" is a video and "youtube.com/playlist" is a course and a hostname
 * cannot tell them apart. `other` is the honest answer when the plan had none of the six.
 */
export type LearningResourceKind =
  | 'book'
  | 'video'
  | 'course'
  | 'practice'
  | 'article'
  | 'doc'
  | 'other'

/**
 * ONE THING TO OPEN — the unit the whole Learning redesign turns on
 * (docs/LEARNING-PLAN-FORMAT.md). A link the plan named, shown as a button that opens it in a new
 * tab. Nothing in LifeOS fetches it, summarises it or checks that it still resolves: it is the
 * plan's own pointer, printed verbatim, and an app that quietly "improved" it would be authoring
 * the curriculum it says it only imports.
 */
export interface LearningResource {
  /** What the owner reads on the button. The plan's own wording. */
  label: string
  /** http(s). Opened in a new tab, never fetched. */
  url: string
  kind: LearningResourceKind
}

export interface LearningTrack {
  id: string
  /**
   * The slug the plan was imported under — and the key a revision is matched on
   * (docs/LEARNING-PLAN-FORMAT.md: "re-importing the same slug REPLACES that track's plan").
   * It is read back rather than write-only because an agent that has to SEND it must be able to
   * READ it; `GET /api/learning/tracks/{trackRef}` accepts it in place of the uuid.
   */
  slug: string
  name: string
  subtitle: string
  currentUnit: string
  itemsTotal: number
  itemsDone: number
  planStartDate: ISODate
  planEndDate: ISODate
  /** Plans are imported, never authored here — provenance is shown in the UI. */
  planVersion: string
  importedAt: ISOTimestamp
  planSource: string
  /**
   * The track's STANDING LIBRARY — the things opened every session, in the order the plan listed
   * them. Empty when the plan named none, and an empty array is shown as a sentence saying so
   * rather than as a section that looks broken.
   */
  resources: LearningResource[]
}

export interface LearningItem {
  id: string
  trackId: string
  trackName: string
  unit: string
  title: string
  /** The day the plan assigns this item to. */
  date: ISODate
  done: boolean
  minutes: number | null
  /**
   * The exact thing to open for this item. NULL when the plan named none — and the row then shows
   * no button at all rather than a dead one pointing at the track's library instead. Hidden is not
   * the same as missing (CLAUDE.md → the app never fakes knowledge).
   */
  resource: LearningResource | null
  /** The PLAN's estimate for this sitting, 5–240. Null when the plan declined to guess. */
  minutesEstimate: number | null
  /** How to do this item well, in the plan's own words. '' when it said nothing. */
  instructions: string
}

export interface LearningStreak {
  /** Consecutive days with at least one completed item, ending at `lastDate`. */
  current: number
  longest: number
  lastDate: ISODate | null
  /** Newest last. One entry per day that had activity. */
  days: Array<{ date: ISODate; minutes: number; items: number }>
}

// ── Interests ───────────────────────────────────────────────────────────────
//
// The saves corpus and the nightly clusters over it (docs/design/handoff/"LifeOS
// Interests.dc.html", option 7a). The page's order is its argument — ask bar first, clusters
// second, library third — and these shapes exist in that order too: a corpus you can talk to,
// a grouping the machine made and admits to, and the individual save last.
//
// TWO RULES THIS BLOCK OBEYS.
//   · A CLUSTER IS NOT A FOLDER. `topic` holds the cluster the nightly job put a save in, and
//     null means it has not been grouped — not "miscellaneous". The screen says "clusters
//     regrouped nightly" for exactly this reason: the grouping is a machine's current opinion,
//     not the owner's filing.
//   · THE AI TWO-LINER CARRIES PROVENANCE OR DOES NOT RENDER. `summary` used to be a bare
//     string, which made a model's sentence indistinguishable from the author's own. It is now
//     an AiArtifact like every other machine-made claim in the app.

/** The provenance contract, reused rather than redeclared — see school.ts for why it is structural. */
import type { AiArtifact } from './school.js'

export type SaveSource = 'reddit' | 'youtube' | 'article' | 'x'

/**
 * The two-line summary of a save. An artifact, not a string: `{ model, sourceRef, generatedAt }`
 * is required, and `hasProvenance()` gates it on the render path (school.ts).
 */
export interface SaveSummary extends AiArtifact {
  text: string
}

export interface InterestSave {
  id: string
  title: string
  url: string
  source: SaveSource
  /**
   * The locator the library card shows: 'r/StableDiffusion', 'YouTube', 'huggingface.co'. The
   * detail pane pairs it with the platform name ('Reddit · r/StableDiffusion'). Length lives in
   * `durationSec`, never in this string — a card that reads "youtube.com · 18 min" cannot be
   * sorted, filtered or asked about by a machine.
   */
  sourceDetail: string
  /** Null means the nightly clustering has not placed it. Absent from every cluster, not filed wrongly. */
  topic: string | null
  savedAt: ISOTimestamp
  unread: boolean
  /**
   * The owner's own words about the save. Never generated, never summarised — the detail pane
   * labels it MY NOTE precisely because everything above it in that panel is not.
   */
  note: string | null
  /** When the note was last touched. Null exactly when `note` is null; the two move together. */
  noteEditedAt: ISOTimestamp | null
  /**
   * The opening of the saved thing, in the author's words, as the capture stored it. Null when
   * the capture got a title and nothing else — an empty excerpt is not a short one.
   */
  excerpt: string | null
  /** The AI two-liner. Null until a model has actually read this save (V4). */
  summary: SaveSummary | null
  durationSec: number | null
}

/**
 * ONE NIGHTLY CLUSTER. Every field but `id`/`label` is DERIVED from the saves in it (README
 * §State Management → "cluster counts" under Derived, never stored) and computed on the server
 * side of the data contract, so an LLM asking the Worker "how big is my AI-video cluster" gets
 * the number the screen shows.
 */
export interface InterestTopic {
  id: string
  label: string
  count: number
  /**
   * The oldest save in the cluster — the screen's quiet "oldest save in this cluster: Mar 2026".
   * It is what tells the owner whether a cluster is a current interest or a fossil.
   */
  oldestSavedAt: ISOTimestamp
  /** The newest save in it. The cluster row is ordered by this: most recently added to, first. */
  newestSavedAt: ISOTimestamp
}

// ── Infrastructure ──────────────────────────────────────────────────────────

export type SyncState = 'ok' | 'error' | 'pending' | 'never'

export interface SyncStatusRow {
  source: string
  lastSyncAt: ISOTimestamp | null
  status: SyncState
  itemsPulled: number
  /** Plain words, not a stack trace. The owner reads this, not a log. */
  lastError: string | null
}

// ── Connections · the trust surface ─────────────────────────────────────────
//
// Everything the Connections screen reports (docs/design/handoff/"LifeOS
// Connections.dc.html", options 12a/12b, README §12). This block is owned by the
// Connections/settings lane — other domains do not read from it.
//
// THE SHAPES CARRY FACTS, THE VIEW CARRIES PHRASING. `lastSyncAt` is a
// timestamp, never "3 days ago"; `queue` is a list, never "3 jobs · 1h 46m
// audio". Every string the mockups show is derived at render time from the
// fields below against the app clock (README §State Management → "Derived,
// never stored"). The one deliberate exception is `ConnectorNote.text`: a
// connector's error or caveat is plain-words copy the connector itself authors
// (the same reason `SyncStatusRow.lastError` is a sentence and not a code), and
// the machine-readable facts behind it — `status`, `retryAt` — sit beside it.

/**
 * A connector's state. `planned` is a real state, not an absence: Oura is
 * designed, named, and visibly incapable of being clicked. `rate_limited` is
 * deliberately NOT an error — there is nothing for the owner to do about it, so
 * it never counts toward "needs attention" and never gets a button.
 * `disconnected` is a BUILT connector with no account connected yet (Gmail before
 * the first Google sign-in): nothing is broken and nothing is stale, so it is not
 * an error either — the owner simply has not connected it.
 */
export type ConnectorStatus = 'ok' | 'error' | 'rate_limited' | 'planned' | 'disconnected'

/**
 * True for a connector that is actually connected to something — working, broken
 * or throttled. A planned connector has never run and a disconnected one has
 * nothing to run against, so neither is "live" for a sync count or a footer.
 */
export function connectorIsLive(connector: Pick<Connector, 'status'>): boolean {
  return connector.status !== 'planned' && connector.status !== 'disconnected'
}

/**
 * The live connectors that have no on-demand sync route on the Worker yet. Empty
 * since Lane 1b (2026-10-02): Gmail gained `POST /api/connections/gmail/sync`.
 * A connector that ships before its sync route goes here until the route lands,
 * so a pull-to-refresh never POSTs to a 404.
 */
const NO_ON_DEMAND_SYNC = new Set<string>()

/** True when a pull-to-refresh (or "Sync now") can ask the Worker to sync this connector. */
export function connectorSyncsOnDemand(connector: Pick<Connector, 'id' | 'status'>): boolean {
  return connectorIsLive(connector) && !NO_ON_DEMAND_SYNC.has(connector.id)
}

export type ConnectorNoteTone = 'error' | 'warning' | 'neutral'

/** The tinted strip under a connector's header: what broke, or what it will never know. */
export interface ConnectorNote {
  tone: ConnectorNoteTone
  /** One sentence, plain words. Names the consequence and at most one action. */
  text: string
}

/**
 * What a broken connector has made stale. Present only while `status` is
 * 'error' — it is what the page-level banner is built from, and a banner with
 * no named consequence is just an error code with better manners.
 */
export interface ConnectorOutage {
  /**
   * The plural noun phrase the banner completes with "… are from <date>":
   * 'assignment due dates and grades on the School screen'. The date itself is
   * NOT stored — it is the day of `lastSyncAt`.
   */
  stale: string
}

export interface Connector {
  id: string
  /** 'Canvas · bCourses' — the card's title. */
  name: string
  /** 'Canvas' — what a sentence calls it ("Reconnect Canvas"). */
  shortName: string
  status: ConnectorStatus
  /** 'bcourses.berkeley.edu · 4 courses · assignments, grades, announcements, files' */
  scope: string
  note: ConnectorNote | null
  outage: ConnectorOutage | null
  /** Null when the connector has never synced — which is always true of a planned one. */
  lastSyncAt: ISOTimestamp | null
  /** How many items the last sync brought in. Null when the count is not known. */
  newItemsLastSync: number | null
  /** Null when never connected: absent, not zero. */
  itemsPulledAllTime: number | null
  /** Only ever set while rate limited. The retry is automatic; the UI offers no button. */
  retryAt: ISOTimestamp | null
  /** Which version brings it. Null unless `status` is 'planned'. */
  plannedFor: string | null
}

export type DesktopWorkerState = 'online' | 'offline'

/** One job the PC has not run yet. Named and dated, so waiting feels finite. */
export interface WorkerJob {
  id: string
  /** 'NEU C61 · Lecture 7' or 'Voice memo · walking home'. */
  title: string
  kind: 'lecture' | 'voice_memo'
  recordedAt: ISOTimestamp
  durationSec: number
  /** What the PC will do with it. One word, shown as a pill. */
  work: 'transcribe'
}

export interface WorkerLastJob {
  /** 'NEU C61 lecture' */
  title: string
  audioSec: number
  transcribedInSec: number
  finishedAt: ISOTimestamp
}

/** Where lecture audio actually lives. Pinned at the bottom of the screen. */
export interface StorageNote {
  machine: string
  archivedBytes: number
  syncedBytes: number
}

export interface DesktopWorkerStatus {
  state: DesktopWorkerState
  /** 'kaz-desktop' */
  machine: string
  /** 'Whisper large-v3'. Null when the worker has never checked in with one. */
  model: string | null
  /** 'idle' | 'transcribing'. Null while offline — the app cannot know. */
  activity: string | null
  lastSeenAt: ISOTimestamp
  /** Null when there has not been one. Absent, not a row of dashes. */
  lastJob: WorkerLastJob | null
  /** In the order the PC will run them. Empty is a real answer. */
  queue: WorkerJob[]
  jobsThisWeek: number
  audioThisWeekSec: number
  storage: StorageNote
}

// (CaptureToken is declared once, in the capture section above — see the collision note there.)

/** The search index, and what is holding the rest of it up. */
export interface IndexStatus {
  itemsIndexed: number
  /** Known items not yet indexed. */
  pending: number
  /** What the rest is waiting on, in plain words: 'Canvas'. Null when nothing waits. */
  blockedBy: string | null
  /** When the indexed part was last confirmed current. Null when not known. */
  currentAsOf: ISOTimestamp | null
}

export type DataMode = 'real' | 'demo'

export interface DataStatus {
  mode: DataMode
  /**
   * How many real journal entries are sitting untouched while demo mode is on.
   * Null when the app cannot know it — and then the demo banner drops the count
   * rather than guessing one.
   */
  realJournalEntries: number | null
  index: IndexStatus
}

/**
 * WHICH MODELLED STATE the demo dataset should answer with. Review-only.
 *
 * Both of the handoff's Connections states have to be reachable to be reviewed,
 * and they are whole-screen states — a broken connector changes the banner, the
 * nav dot, the section order and the header counts at once. So the demo dataset
 * models both and the route selects one from `?state=`. `remote` ignores this
 * argument entirely: the real Worker has exactly one state, the true one.
 */
export type ConnectionsScenario =
  /** 12a — Canvas token expired, worker online, real data. */
  | 'connector_broken'
  /** 12b — Canvas healthy, PC asleep with a named queue, demo mode on. */
  | 'worker_offline'
